import { useState, useEffect } from "react";
import axios from "axios";
import { FolderOpen, ExternalLink, BookOpen, Video, FileText } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "cybertruck", label: "Cybertruck" },
  { id: "starlink", label: "Starlink" },
  { id: "tesla", label: "Tesla" },
  { id: "general", label: "General" },
];

const TYPE_ICONS = {
  guide: BookOpen,
  tutorial: Video,
  document: FileText,
};

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const url = category ? `${API}/resources?category=${category}` : `${API}/resources`;
        const res = await axios.get(url);
        setResources(res.data);
      } catch (err) {
        console.error("Resources error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category]);

  return (
    <div data-testid="resources-page" className="min-h-screen pt-20 pb-12 px-6 md:px-12" style={{ background: '#0A0A0C' }}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <span className="text-xs tracking-[0.2em] uppercase text-[#22C55E] font-semibold">LIBRARY</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Resource Library
          </h1>
          <p className="text-base text-slate-400 mt-3">Guides, tutorials, and documentation to help you succeed.</p>
        </div>

        <div className="flex gap-2 mb-10 overflow-x-auto">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              data-testid={`resource-filter-${cat.label.toLowerCase()}`}
              className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
                category === cat.id
                  ? "bg-[#E31937] text-white"
                  : "bg-[#141417] border border-[#2A2A30] text-slate-400 hover:text-white hover:border-white/30"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : resources.length === 0 ? (
          <div className="bg-[#141417] border border-[#2A2A30] p-12 text-center">
            <FolderOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No Resources Found</h3>
            <p className="text-sm text-slate-400">Check back soon for new guides and tutorials.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-[#2A2A30]">
            {resources.map((res, i) => {
              const TypeIcon = TYPE_ICONS[res.type] || FileText;
              return (
                <motion.div
                  key={res.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`resource-card-${res.id}`}
                  className="bg-[#141417] border border-[#2A2A30] p-6 hover:bg-[#1C1C20] transition-all duration-300 group"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <TypeIcon className="w-5 h-5 text-[#00BFFF]" />
                    <span className="text-xs uppercase tracking-wider text-slate-500">{res.type}</span>
                    <span className="ml-auto text-xs uppercase tracking-wider text-slate-600">{res.category}</span>
                  </div>
                  <h3 className="text-base font-bold text-white mb-2 group-hover:text-[#E31937] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {res.title}
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">{res.description}</p>
                  <a
                    href={res.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`resource-link-${res.id}`}
                    className="inline-flex items-center gap-1 text-xs font-bold uppercase text-[#00BFFF] hover:text-[#33CCFF] transition-colors"
                  >
                    Open Resource <ExternalLink className="w-3 h-3" />
                  </a>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
